'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["position"];
import PropTypes from 'prop-types';
import { Fragment, useState } from 'react';
import PortalIdParser from 'PortalIdParser';
import { getFullUrl } from 'hubspot-url-utils';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIToggleGroup from 'UIComponents/input/UIToggleGroup';
import UISelect from 'UIComponents/input/UISelect';
import UIRadioInput from 'UIComponents/input/UIRadioInput';
import UIButton from 'UIComponents/button/UIButton';
import Small from 'UIComponents/elements/Small';
import { VideoOverlayTypes, VideoOverlayPositions } from '../../constants/video';
import ConversionAssetSummary from './ConversionAssetSummary';
import ConversionAssetOptionsPanel from './ConversionAssetOptionsPanel';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import VideoFormSelect from './VideoFormSelect';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_OVERLAY_POSITION = VideoOverlayPositions.AFTER;
const renderOverlaySelect = ({
  overlayType,
  overlayGuid,
  disabled,
  onOpenPicker,
  onUpdateConversionAsset,
  formInfo,
  fetchFormInfo,
  fetchForms,
  trackVideoInteraction
}) => {
  const useFormSelect = overlayType === VideoOverlayTypes.FORM;
  const handleOpenPicker = () => {
    if (overlayType === VideoOverlayTypes.FORM) {
      window.open(`${getFullUrl('app')}/forms/${PortalIdParser.get()}`);
    } else {
      onOpenPicker();
    }
  };
  if (useFormSelect) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: `RichTextLib.video.overlay.${overlayType}.label`,
        linkText: 'RichTextLib.video.overlay.createOrEditForm',
        onLinkClick: handleOpenPicker,
        required: true
      }), /*#__PURE__*/_jsx(VideoFormSelect, {
        formInfo: formInfo,
        selectedGuid: overlayGuid,
        fetchForms: fetchForms,
        readOnly: false,
        onChange: id => {
          fetchFormInfo(id);
          onUpdateConversionAsset({
            type: VideoOverlayTypes.FORM,
            id,
            position: DEFAULT_OVERLAY_POSITION
          });
        },
        trackVideoInteraction: trackVideoInteraction
      })]
    });
  }
  return /*#__PURE__*/_jsx(UIFormControl, {
    className: "m-top-1",
    label: I18n.text(`RichTextLib.video.overlay.${overlayType}.label`),
    required: true,
    children: /*#__PURE__*/_jsx(UIButton, {
      "data-test-id": "insert-overlay-button",
      use: "tertiary-light",
      disabled: disabled,
      size: "small",
      onClick: handleOpenPicker,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `RichTextLib.video.overlay.${overlayType}.button`
      })
    })
  });
};
const renderRadioOption = props => {
  const {
      position
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(UIRadioInput, Object.assign({}, rest, {
    "data-test-id": `overlay-position-${position}`,
    name: `display${position}`,
    value: position,
    size: "small",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `RichTextLib.video.overlay.display.${position}`
    })
  }));
};
const propTypes = {
  className: PropTypes.string,
  dropdownClassName: PropTypes.string,
  conversionAsset: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.string,
    position: PropTypes.string
  }),
  ctaName: PropTypes.string,
  formInfo: PropTypes.object,
  onUpdateConversionAsset: PropTypes.func.isRequired,
  onOpenConversionAssetPanel: PropTypes.func,
  onOpenCtaPicker: PropTypes.func.isRequired,
  updateCtaInfo: PropTypes.func.isRequired,
  updateFormInfo: PropTypes.func.isRequired,
  clearCtaInfo: PropTypes.func.isRequired,
  clearFormInfo: PropTypes.func.isRequired,
  fetchFormInfo: PropTypes.func.isRequired,
  fetchForms: PropTypes.func.isRequired,
  trackVideoInteraction: PropTypes.func,
  useConversionAssetOptionsPanel: PropTypes.bool,
  video: PropTypes.shape({
    videoId: PropTypes.number,
    duration: PropTypes.number
  })
};
function VideoPopoverOverlayControls(props) {
  const {
    className,
    dropdownClassName,
    conversionAsset,
    onUpdateConversionAsset,
    onOpenCtaPicker,
    onOpenConversionAssetPanel,
    updateCtaInfo,
    clearCtaInfo,
    clearFormInfo,
    ctaName,
    formInfo: _formInfo = {},
    fetchFormInfo,
    fetchForms,
    useConversionAssetOptionsPanel,
    trackVideoInteraction = () => {},
    video
  } = props;
  const [isOverlayOptionsPanelOpen, setIsOverlayOptionsPanelOpen] = useState(false);
  const {
    type: overlayType = VideoOverlayTypes.NONE,
    id: overlayGuid,
    position: overlayPosition
  } = conversionAsset || {};
  let overlayName;
  if (overlayType === VideoOverlayTypes.CTA) {
    overlayName = ctaName;
  } else if (overlayType === VideoOverlayTypes.FORM) {
    overlayName = _formInfo.name;
  }
  const overlayOptions = Object.values(VideoOverlayTypes).map(type => ({
    text: I18n.text(`RichTextLib.video.overlay.${type}.option`),
    value: type
  }));
  const handleOpenPicker = guid => {
    if (overlayType === VideoOverlayTypes.CTA) {
      onOpenCtaPicker(guid, (updatedGuid, data) => {
        updateCtaInfo(data);
        onUpdateConversionAsset({
          type: overlayType,
          id: updatedGuid,
          position: DEFAULT_OVERLAY_POSITION
        });
      });
    }
  };
  const handleOverlayTypeChange = evt => {
    const {
      value
    } = evt.target;
    let updatedConversionAsset = null;
    if (value !== VideoOverlayTypes.NONE) {
      updatedConversionAsset = {
        type: value
      };
    }
    onUpdateConversionAsset(updatedConversionAsset);
    clearCtaInfo();
    clearFormInfo();
    trackVideoInteraction(`select-overlay-type-${value.toLowerCase()}`);
  };
  const handleOverlayPositionChange = evt => {
    const {
      value
    } = evt.target;
    onUpdateConversionAsset(Object.assign({}, conversionAsset, {
      position: value
    }));
  };
  const handleRemoveOverlay = () => {
    onUpdateConversionAsset({
      type: overlayType
    });
    clearCtaInfo();
    clearFormInfo();
  };
  const handleEditConversionAssetOptions = () => {
    if (onOpenConversionAssetPanel) {
      onOpenConversionAssetPanel(video, conversionAsset, updatedAsset => {
        onUpdateConversionAsset(updatedAsset);
      });
    } else {
      setIsOverlayOptionsPanelOpen(true);
    }
  };
  const renderDisplayOptions = () => {
    if (!useConversionAssetOptionsPanel) {
      return /*#__PURE__*/_jsx(UIFormControl, {
        className: "m-top-1",
        label: I18n.text('RichTextLib.video.overlay.display.label'),
        children: /*#__PURE__*/_jsx(UIToggleGroup, {
          name: "videoOverlayDisplayOptions",
          children: Object.values(VideoOverlayPositions).map(position => renderRadioOption({
            key: position,
            position,
            checked: overlayPosition === position,
            disabled: !overlayGuid,
            onClick: handleOverlayPositionChange
          }))
        })
      });
    }
    if (!(conversionAsset && conversionAsset.type && conversionAsset.id)) {
      return null;
    }
    return /*#__PURE__*/_jsxs("div", {
      className: "m-top-2",
      children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
        primaryText: 'RichTextLib.video.overlay.display.labelWithOptionsPanel',
        linkText: 'RichTextLib.video.conversionAssetSummary.edit',
        onLinkClick: handleEditConversionAssetOptions,
        "data-test-id": 'edit-conversion-asset-options'
      }), /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(ConversionAssetSummary, {
          className: "m-bottom-0 p-left-6",
          conversionAsset: conversionAsset
        })
      })]
    });
  };
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(UIFieldset, {
      className: className,
      _size: "small",
      children: [/*#__PURE__*/_jsx(UIFormControl, {
        label: I18n.text(useConversionAssetOptionsPanel ? 'RichTextLib.video.overlay.labelWithOptionsPanel' : 'RichTextLib.video.overlay.label'),
        children: /*#__PURE__*/_jsx(UISelect, {
          "data-test-id": "video-overlay-select",
          dropdownClassName: dropdownClassName,
          value: overlayType,
          options: overlayOptions,
          defaultValue: VideoOverlayTypes.NONE,
          searchable: false,
          onChange: handleOverlayTypeChange
        })
      }), renderOverlaySelect({
        overlayType,
        overlayGuid,
        overlayName,
        disabled: overlayType === VideoOverlayTypes.NONE,
        formInfo: _formInfo,
        fetchForms,
        fetchFormInfo,
        useConversionAssetOptionsPanel,
        onOpenPicker: handleOpenPicker,
        onRemoveOverlay: handleRemoveOverlay,
        onUpdateConversionAsset,
        trackVideoInteraction
      }), renderDisplayOptions()]
    }), isOverlayOptionsPanelOpen && /*#__PURE__*/_jsx(ConversionAssetOptionsPanel, {
      conversionAsset: conversionAsset,
      video: video,
      onClose: () => setIsOverlayOptionsPanelOpen(false),
      onSubmit: asset => {
        onUpdateConversionAsset(asset);
        setIsOverlayOptionsPanelOpen(false);
      },
      trackVideoInteraction: trackVideoInteraction
    })]
  });
}
VideoPopoverOverlayControls.propTypes = propTypes;
VideoPopoverOverlayControls.displayName = 'VideoPopoverOverlayControls';
export default VideoPopoverOverlayControls;